import type { RequestPresence } from './queue-presence';

export type SongDisplayInput = {
  nonlistSong: string | null;
  // Live learns requested with their parts named carry these; ones requested
  // before the split, or as a single string, carry only nonlistSong.
  nonlistTitle?: string | null;
  nonlistArtist?: string | null;
  // Optional because some payloads omit the song object entirely on a row that
  // never had one.
  song?: { title: string; artist: string } | null;
};

export type RequestDisplayInput = {
  name: string;
  amount: number;
  user: { username: string } | null;
  requestText: string;
  source: string;
  // Chat presence, when the viewer is allowed to see it and it is known. Every
  // surface other than the queue omits both fields, which reads as unknown and
  // renders exactly as it did before presence existed.
  id?: number;
  presence?: RequestPresence | null;
};

// Keyed by request `source` as well as the bare `user.platform` names, which
// requests carry alongside it.
const PLATFORM_LABEL_KEYS: Readonly<Record<string, string>> = {
  twitch: 'queue.source.twitch',
  'twitch-chat': 'queue.source.twitch',
  'twitch-redemption': 'queue.source.twitch',
  extension: 'queue.source.twitch',
  youtube: 'queue.source.youtube',
  'youtube-chat': 'queue.source.youtube',
  kick: 'queue.source.kick',
  streamlabs: 'queue.source.streamlabs',
  streamelements: 'queue.source.streamelements',
  website: 'queue.source.website',
  'website-anonymous': 'queue.source.website',
  manual: 'queue.source.manual',
};

export function getPlatformLabelKey(source: string): string | null {
  return PLATFORM_LABEL_KEYS[source] ?? null;
}

export function getSongTitle(item: SongDisplayInput): string {
  // A live learn still carries a song object: the API serializes it as a value
  // struct, so its fields arrive as empty strings rather than null. `??` would
  // keep that empty string, which is why each step falls through on falsy.
  return item.nonlistTitle || item.nonlistSong || item.song?.title || '';
}

export function getSongArtist(item: SongDisplayInput): string {
  // A live learn falls back to no artist rather than the songlist row's, which
  // is empty for it anyway — the nested song object is still present but blank.
  return item.nonlistSong || item.nonlistTitle
    ? (item.nonlistArtist ?? '')
    : (item.song?.artist ?? '');
}

export function getSongDisplay(item: SongDisplayInput): string {
  const title = getSongTitle(item);
  const artist = getSongArtist(item);
  return artist ? `${title} - ${artist}` : title;
}

/*
 * The identity the request is attributed to is read fresh on every query, so it
 * is what a viewer is called right now. name is the value stored on the request
 * itself and stands in wherever no user resolved — anonymous requests, tips that
 * matched nobody, a moderator naming somebody the platform could not place — and
 * for the rows written before an attributed request stopped carrying a copy,
 * where the stored spelling is the stale one.
 */
export function resolveDisplayName(request: RequestDisplayInput): string {
  return request.user?.username || request.name || '';
}

export function getRequestedBy(requests: ReadonlyArray<RequestDisplayInput> | null): string {
  if (!requests || requests.length === 0) {
    return '';
  }

  const seen = new Set<string>();
  const unique: Array<{ name: string; amount: number }> = [];
  for (const r of requests) {
    const displayName = resolveDisplayName(r);
    if (!seen.has(displayName)) {
      seen.add(displayName);
      unique.push({ name: displayName, amount: r.amount });
    }
  }

  const showAmounts = unique.length > 1;

  return unique
    .map((entry) =>
      showAmounts && entry.amount > 0 ? `${entry.name} (${entry.amount})` : entry.name,
    )
    .join(', ');
}
